import { FC, useEffect, useState, useCallback } from 'react';
import { css } from '@emotion/react';
import { useQuery, useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Page from '../Page';
import Button from '../Button';
import Header from '../Header';
import { spacing, fontStyles } from '../../styles';
import {
  getNotifications,
  markMultipleNotificationsRead,
  NotificationItem,
  PaginatedNotifications,
} from '../../apis/backend';
import { handleNotificationRoute } from '../../utils/routing';
import { PushNotificationService } from '../../services/pushNotificationService';
import { reportError } from '../../utils/reporting';

// Helper function to safely format dates
const formatNotificationDate = (dateString: string | null | undefined, t: (key: string) => string): string => {
  if (!dateString) return t('notifications.unknownDate');

  try {
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      // Malformed date from backend - report for monitoring
      reportError(new Error('Invalid date string from notification'), {
        component: 'NotificationHistoryPage',
        dateString,
      });
      return t('notifications.invalidDate');
    }

    const dateStr = date.toLocaleDateString();
    const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    return `${dateStr} ${timeStr}`;
  } catch (error) {
    // Date parsing error - report for monitoring malformed backend data
    reportError(error instanceof Error ? error : new Error('Date parsing failed'), {
      component: 'NotificationHistoryPage',
      dateString,
    });
    return t('notifications.invalidDate');
  }
};

// Helper function to handle notification click
const handleNotificationClick = (notification: NotificationItem) => {
  const route = notification.route;
  handleNotificationRoute(route || '/portfolios');
};

const NotificationHistoryPage: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [fcmToken, setFcmToken] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [isLoadingFromBackend, setIsLoadingFromBackend] = useState(false);
  const [paginationInfo, setPaginationInfo] = useState<PaginatedNotifications['pagination'] | null>(null);

  // Pull-to-refresh state
  const [isPullToRefreshing, setIsPullToRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [startY, setStartY] = useState(0);
  const [canPull, setCanPull] = useState(false);

  // Query client for cache invalidation
  const queryClient = useQueryClient();

  const PULL_THRESHOLD = 80; // Distance needed to trigger refresh

  useEffect(() => {
    // Only try to get token if there are enabled notification preferences
    const checkNotificationPreferences = () => {
      const savedPreferences = localStorage.getItem('notificationPreferences');
      if (!savedPreferences) {
        return false;
      }

      try {
        const preferences = JSON.parse(savedPreferences);
        const enabledTypes = preferences.filter((pref: { enabled: boolean }) => pref.enabled);
        return enabledTypes.length > 0;
      } catch (error) {
        // JSON parsing errors for corrupted localStorage data - report but don't break the app
        reportError(error instanceof Error ? error : new Error('Failed to parse notification preferences'), {
          component: 'NotificationHistoryPage',
          savedPreferences,
        });
        return false;
      }
    };

    if (checkNotificationPreferences()) {
      PushNotificationService.getToken()
        .then((token) => setFcmToken(token))
        .catch(() => setFcmToken(null));
    } else {
      setFcmToken(null);
    }
  }, []);

  // Auto-mark unread notifications as read when user views them
  const autoMarkAsRead = useCallback(
    async (notifications: NotificationItem[]) => {
      if (!fcmToken) return;

      if (notifications.length > 0) {
        try {
          await markMultipleNotificationsRead(fcmToken);

          // Invalidate the unread count cache to update badges
          await queryClient.invalidateQueries(['unread-notifications-count']);
          await queryClient.refetchQueries(['unread-notifications-count'], { active: true });
        } catch (error) {
          console.error('[NotificationHistoryPage] Failed to mark notifications as read:', error);
        }
      }
    },
    [fcmToken, queryClient],
  );

  // Fetch fresh notifications from backend - always real-time
  const fetchNotifications = useCallback(async () => {
    if (!fcmToken) return;
    try {
      setIsLoadingFromBackend(true);

      // Clear any existing cache
      await queryClient.invalidateQueries(['notifications-pagination']);

      const response = await getNotifications(fcmToken, 1, 20);
      // Filter out smart summaries - they should not appear in notification history
      const freshNotifications = response.notifications.filter((notification) => notification.type !== 'smart_summary');

      // Update UI with fresh data
      setItems(freshNotifications);
      setPage(1);

      // Save pagination info from first page
      setPaginationInfo(response.pagination);

      // Auto-mark unread notifications as read
      await autoMarkAsRead(freshNotifications);
    } catch (error) {
      console.error('[NotificationHistoryPage] Failed to fetch notifications:', error);
      setItems([]); // Clear on error
      setPaginationInfo(null); // Clear pagination info on error
    } finally {
      setIsLoadingFromBackend(false);
    }
  }, [fcmToken, queryClient, autoMarkAsRead]);

  // Fetch notifications when fcmToken is available or component mounts
  useEffect(() => {
    if (fcmToken) {
      fetchNotifications();
    }
  }, [fcmToken, fetchNotifications]);

  // Clear cache when component mounts
  useEffect(() => {
    queryClient.removeQueries(['notifications-pagination']);

    // Clean up any old localStorage data
    try {
      localStorage.removeItem('cachedNotifications');
      localStorage.removeItem('lastNotificationsFetch');
    } catch (error) {
      // localStorage errors (e.g., Safari private mode) - report but don't break the app
      reportError(error instanceof Error ? error : new Error('Failed to clean localStorage'), {
        component: 'NotificationHistoryPage',
        action: 'localStorage cleanup',
      });
    }
  }, [queryClient]);

  // Pull-to-refresh handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    setStartY(touch.clientY);

    // Only allow pull-to-refresh if we're at the top of the page
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    setCanPull(scrollTop === 0);
    setPullDistance(0);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!canPull || isPullToRefreshing || isLoadingFromBackend) return;

    const touch = e.touches[0];
    const currentY = touch.clientY;
    const diff = currentY - startY;

    if (diff > 0) {
      // Prevent default scroll behavior when pulling down
      e.preventDefault();

      // Calculate pull distance with some resistance
      const distance = Math.min(diff * 0.5, PULL_THRESHOLD * 1.5);
      setPullDistance(distance);
    }
  };

  const handleTouchEnd = async () => {
    if (!canPull || isPullToRefreshing || isLoadingFromBackend) {
      setPullDistance(0);
      setCanPull(false);
      return;
    }

    if (pullDistance >= PULL_THRESHOLD) {
      setIsPullToRefreshing(true);
      try {
        await fetchNotifications();
      } finally {
        setIsPullToRefreshing(false);
      }
    }

    setPullDistance(0);
    setCanPull(false);
  };

  // Load more pages (only when not loading from backend)
  const { data: paginationData, isLoading: isPaginationLoading } = useQuery(
    ['notifications-pagination', fcmToken, page],
    () => (fcmToken && page > 1 ? getNotifications(fcmToken, page, 20) : null),
    {
      enabled: !!fcmToken && page > 1 && !isLoadingFromBackend && !isPullToRefreshing,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      staleTime: 0, // Always consider data stale
      cacheTime: 1000 * 60 * 5, // Cache for 5 minutes but mark as stale immediately
      onSuccess: (res) => {
        if (res?.notifications) {
          // Filter out smart summaries - they should not appear in notification history
          const filteredNotifications = res.notifications.filter(
            (notification) => notification.type !== 'smart_summary',
          );
          setItems((prev) => [...prev, ...filteredNotifications]);
        }
      },
    },
  );

  const hasMore = Boolean(paginationData?.pagination.hasNext || (page === 1 && paginationInfo?.hasNext)); // Consider pagination from both first page and subsequent pages

  const unreadStyles = css`
    font-weight: 700;
  `;

  const pullToRefreshStyles = css`
    position: relative;
    transition: transform 0.2s ease-out;
    transform: translateY(${pullDistance}px);
    touch-action: pan-y;
    overscroll-behavior: contain;
  `;

  const pullIndicatorStyles = css`
    position: absolute;
    top: -60px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: var(--card-background);
    border: 1px solid var(--border-color);
    opacity: ${Math.min(pullDistance / PULL_THRESHOLD, 1)};
    transition: opacity 0.1s ease-out;
    ${fontStyles.cat};
    color: var(--secondary-text);
  `;

  const getRefreshIconRotation = () => {
    if (isPullToRefreshing) return 'rotate(360deg)';
    return `rotate(${Math.min((pullDistance / PULL_THRESHOLD) * 180, 180)}deg)`;
  };

  return (
    <Page showNavigationBar>
      <Header headingText={t('notifications.title')} onBack={() => history.goBack()} variant="left-aligned" />
      <div
        css={css`
          ${pullToRefreshStyles};
          margin-top: 60px; /* Space for fixed header */
        `}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Pull-to-refresh indicator */}
        {(pullDistance > 0 || isPullToRefreshing) && (
          <div css={pullIndicatorStyles}>
            <div
              css={css`
                transition: transform ${isPullToRefreshing ? '1s' : '0.2s'} ease-out;
                transform: ${getRefreshIconRotation()};
                animation: ${isPullToRefreshing ? 'spin 1s linear infinite' : 'none'};

                @keyframes spin {
                  from {
                    transform: rotate(0deg);
                  }
                  to {
                    transform: rotate(360deg);
                  }
                }
              `}
            >
              ↻
            </div>
          </div>
        )}

        {/* Loading indicator */}
        {(isLoadingFromBackend || isPullToRefreshing) && (
          <div
            css={css`
              padding: ${spacing.deci};
              text-align: center;
              ${fontStyles.cat};
              color: var(--secondary-text);
              background-color: var(--card-background);
              border-radius: 8px;
              margin-bottom: ${spacing.deci};
              border: 1px solid var(--border-color);
            `}
          >
            {isPullToRefreshing ? t('notifications.refreshing') : t('notifications.loading')}
          </div>
        )}

        {/* Empty state */}
        {items.length === 0 && !isLoadingFromBackend && !isPullToRefreshing && (
          <div
            css={css`
              padding: ${spacing.hecto};
              text-align: center;
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: ${spacing.deci};
            `}
          >
            <div
              css={css`
                ${fontStyles.cat};
                color: var(--secondary-text);
              `}
            >
              {t('notifications.noNotificationsYet')}
            </div>
            <Button variant="secondary" block onClick={() => history.push('/notification-customize')}>
              {t('notifications.configureAlerts')}
            </Button>
          </div>
        )}

        {/* Notifications */}
        {items.map((notification) => (
          <div
            key={notification.id}
            css={css`
              display: flex;
              align-items: flex-start;
              gap: ${spacing.deci};
              padding: ${spacing.deci};
              border-bottom: 1px solid var(--border-color);
              transition: background-color 0.2s ease;
              cursor: pointer;

              &:hover {
                background-color: var(--hover-background, rgba(0, 0, 0, 0.05));
              }

              @media (prefers-color-scheme: dark) {
                &:hover {
                  background-color: var(--hover-background, rgba(255, 255, 255, 0.05));
                }
              }
            `}
            onClick={() => handleNotificationClick(notification)}
          >
            <div
              css={css`
                flex: 1;
                min-width: 0; /* Allow text to truncate */
              `}
            >
              <div css={notification.read ? undefined : unreadStyles}>{notification.title}</div>
              <div
                css={css`
                  ${fontStyles.cat};
                  color: var(--tertiary-text);
                  margin-top: ${spacing.micro};
                  line-height: 1.4;
                `}
              >
                {notification.body}
              </div>
              <div
                css={css`
                  ${fontStyles.cat};
                  color: var(--tertiary-text);
                  margin-top: ${spacing.micro};
                  font-size: 0.75rem;
                `}
              >
                {formatNotificationDate(notification.sentAt, t)}
              </div>
            </div>
          </div>
        ))}

        {hasMore && !isLoadingFromBackend && !isPullToRefreshing && (
          <Button
            onClick={() => setPage((p) => p + 1)}
            css={css`
              margin-top: ${spacing.deci};
            `}
            block
            variant="secondary"
            disabled={isPaginationLoading}
          >
            {isPaginationLoading ? t('common.loading') : t('notifications.loadMore')}
          </Button>
        )}
      </div>
    </Page>
  );
};

export default NotificationHistoryPage;
